import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStore } from '@/stores/useStore'
import { FiMenu, FiX, FiUser } from 'react-icons/fi'
import { useState } from 'react'
import LoginModal from './LoginModal'

export default function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const { isAuthenticated, walletAddress, logout } = useStore()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/create', label: t('nav.create') },
    { path: '/dashboard', label: t('nav.dashboard') },
  ]

  const isActive = (path: string) => location.pathname === path

  return (
    <>
      <header className="bg-luxury-navy-900/95 backdrop-blur-sm border-b border-luxury-gold-800/30 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-luxury-gold-500 to-copper rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <span className="text-white font-serif font-bold text-xl">Snap 3D</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`transition-colors ${
                    isActive(item.path)
                      ? 'text-luxury-gold-500'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Auth Section */}
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 bg-luxury-navy-800 rounded-lg px-4 py-2 hover:bg-luxury-navy-700 transition-colors"
                  >
                    <FiUser className="w-4 h-4 text-luxury-gold-500" />
                    <span className="text-sm text-white">
                      {walletAddress?.slice(0, 4)}...{walletAddress?.slice(-4)}
                    </span>
                  </Link>
                  <button
                    onClick={logout}
                    className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setLoginModalOpen(true)}
                  className="px-6 py-2 bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-500 text-luxury-navy-950 font-semibold rounded-lg hover:from-luxury-gold-500 hover:to-luxury-gold-400 transition-colors"
                >
                  {t('nav.login')}
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-luxury-gold-800/30">
              <nav className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`transition-colors ${
                      isActive(item.path)
                        ? 'text-luxury-gold-500'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      logout()
                      setMobileMenuOpen(false)
                    }}
                    className="text-left text-gray-300 hover:text-white transition-colors"
                  >
                    {t('nav.logout')}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setLoginModalOpen(true)
                      setMobileMenuOpen(false)
                    }}
                    className="text-left text-luxury-gold-500"
                  >
                    {t('nav.login')}
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </>
  )
}
